import type { HTMLAttributes } from "react";
import { cn } from "../lib/cn";

/*
 * Badge / chip tones per the 2026-05-12 design system handoff.
 *
 * Three families:
 *
 *   - tier  · routine / timely / important. The Attention card and any
 *             inline tier indicator. Coloured per the attention token
 *             tier (bg / border / ink); the top tier's tone keeps the
 *             token's `urgent` name, which is shared with the ops severity
 *             pills and the generic alert surfaces.
 *
 *   - status · active / new / paused / graduated / good / watch /
 *              alert. The signal row status pill plus the action-card
 *              status dot. Coloured per intent. `helped` is the
 *              affirmative-recap variant (leaf-soft fill).
 *
 *   - kind  · habit / reset / recipe / article / story. Content-type
 *             chip used in plan lists, library cards. Each kind has
 *             its own ink colour (teal / orange / purple / amber)
 *             pulled from --color-chip-* tokens.
 */
export type BadgeTone =
  | "routine"
  | "timely"
  | "urgent"
  | "habit"
  | "reset"
  | "recipe"
  | "article"
  | "story"
  | "active"
  | "new"
  | "paused"
  | "graduated"
  | "good"
  | "watch"
  | "alert"
  | "helped";

const TONES: Record<BadgeTone, string> = {
  // Tier badges - match the attention-card surface treatments.
  routine:
    "bg-attention-routine-bg text-attention-routine-ink border border-attention-routine-border",
  timely: "bg-attention-timely-bg text-attention-timely-ink border border-attention-timely-border",
  urgent: "bg-attention-urgent-bg text-attention-urgent-ink border border-attention-urgent-border",

  // Content-kind chips.
  habit:
    "bg-leaf-soft text-leaf-dark border border-[color-mix(in_srgb,var(--color-leaf)_30%,transparent)]",
  reset: "bg-teal-50 text-teal-700 border border-teal-200",
  recipe:
    "bg-[color-mix(in_srgb,var(--color-chip-recipe)_6%,transparent)] text-[var(--color-chip-recipe)] border border-[color-mix(in_srgb,var(--color-chip-recipe)_30%,transparent)]",
  article:
    "bg-[color-mix(in_srgb,var(--color-chip-article)_6%,transparent)] text-[var(--color-chip-article)] border border-[color-mix(in_srgb,var(--color-chip-article)_30%,transparent)]",
  story:
    "bg-[color-mix(in_srgb,var(--color-chip-story)_6%,transparent)] text-[var(--color-chip-story)] border border-[color-mix(in_srgb,var(--color-chip-story)_30%,transparent)]",

  // Status chips - used as inline dots or pills.
  active: "bg-teal-50 text-teal-700",
  new: "bg-leaf-soft text-leaf-dark",
  paused: "bg-mist text-gray-500",
  graduated:
    "bg-[color-mix(in_srgb,var(--color-chip-story)_8%,transparent)] text-[var(--color-chip-story)]",
  good: "bg-leaf-soft text-leaf-dark",
  watch: "bg-attention-timely-bg text-attention-timely-ink",
  alert: "bg-orange-100 text-orange-700",
  helped:
    "bg-leaf-soft text-leaf-dark border border-[color-mix(in_srgb,var(--color-leaf)_30%,transparent)]",
};

export function Badge({
  tone = "routine",
  className,
  children,
  ...rest
}: HTMLAttributes<HTMLSpanElement> & { tone?: BadgeTone }) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-[0.14em]",
        TONES[tone],
        className,
      )}
      {...rest}
    >
      {children}
    </span>
  );
}
